import {
  Home,
  Users,
  MapPin,
  User,
  Building,
  Plus,
  LogOut,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import Logo from "@/assets/logo.png"; // Ajuste o caminho conforme necessário

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  isAdmin?: boolean;
}

interface NavigationItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
}

export function Sidebar({
  sidebarOpen,
  setSidebarOpen,
  isAdmin = true,
}: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);

  const navigationItems: NavigationItem[] = [
    {
      path: "/home",
      label: "Dashboard",
      icon: Home,
    },
    {
      path: "/usuarios",
      label: "Usuários",
      icon: Users,
      adminOnly: true,
    },
    {
      path: "/regioes",
      label: "Regiões",
      icon: MapPin,
      adminOnly: true,
    },
    {
      path: "/proprietarios",
      label: "Proprietários",
      icon: User,
      adminOnly: true,
    },
    {
      path: "/condominios",
      label: "Edifícios",
      icon: Building,
      adminOnly: true,
    },
    {
      path: "/cadastro-propriedade",
      label: "Novo Imóvel",
      icon: Plus,
      adminOnly: true,
    },
  ];

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  const isActivePath = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path);
  };

  const getButtonClasses = (path: string) => {
    const isActive = isActivePath(path);
    return `flex w-full items-center rounded-lg px-4 py-3 text-left text-sm font-medium transition-all duration-200 ${
      isActive
        ? "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg transform scale-[1.02]"
        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
    }`;
  };

  const getIconClasses = (path: string) => {
    const isActive = isActivePath(path);
    return `mr-3 h-5 w-5 ${isActive ? "text-white" : "text-gray-500"}`;
  };

  const adminItems = navigationItems.filter((item) => item.adminOnly);
  const generalItems = navigationItems.filter((item) => !item.adminOnly);

  return (
    <>
      {/* Sidebar */}
      <div
        className={`${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transition-transform duration-300 ease-in-out lg:static lg:inset-0 lg:translate-x-0`}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="relative flex h-20 items-center justify-center overflow-hidden border-b border-gray-200 bg-white">
            <div className="relative z-10 flex items-center space-x-2">
              <img src={Logo} width={120} alt="Logo" />
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 p-4">
            {/* General Items */}
            {generalItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false); // Fecha sidebar no mobile após navegação
                  }}
                  className={getButtonClasses(item.path)}
                >
                  <IconComponent className={getIconClasses(item.path)} />
                  {item.label}
                </button>
              );
            })}

            {/* Admin Section */}
            {isAdmin && adminItems.length > 0 && (
              <>
                <div className="pt-4">
                  <p className="px-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Administração
                  </p>
                </div>
                {adminItems.map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <button
                      key={item.path}
                      onClick={() => {
                        navigate(item.path);
                        setSidebarOpen(false); // Fecha sidebar no mobile após navegação
                      }}
                      className={getButtonClasses(item.path)}
                    >
                      <IconComponent className={getIconClasses(item.path)} />
                      {item.label}
                    </button>
                  );
                })}
              </>
            )}

            {/* Logout Button */}
            <div className="pt-4">
              <Dialog open={logoutModalOpen} onOpenChange={setLogoutModalOpen}>
                <DialogTrigger asChild>
                  <button className="flex w-full items-center rounded-lg px-4 py-3 text-left text-sm font-medium text-red-700 transition-all duration-200 hover:bg-red-50">
                    <LogOut className="mr-3 h-5 w-5 text-red-500" />
                    Sair
                  </button>
                </DialogTrigger>
                <DialogContent className="rounded-2xl bg-white p-6 shadow-2xl sm:max-w-[425px]">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-bold text-gray-900">
                      Confirmar Logout
                    </DialogTitle>
                    <DialogDescription className="text-gray-600">
                      Tem certeza que deseja sair do sistema?
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter className="gap-2">
                    <Button
                      className="rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200"
                      onClick={() => setLogoutModalOpen(false)}
                    >
                      Cancelar
                    </Button>
                    <Button
                      className="rounded-xl bg-red-600 text-white hover:bg-red-700"
                      onClick={handleLogout}
                    >
                      Sair
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </nav>
        </div>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </>
  );
}
